// Vendor Libs
import React, { Component } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { get, isEmpty, map } from 'lodash';

// Components
import Button from '../core/button';
import Checkbox from '../core/checkbox';
import DateRange from '../core/date-range';
import Icon from '../core/icon';
import Input from '../core/input';
import LanguageSelect from '../app/language-select';
import MassActionTable from './mass-action-table';
import MultiSelect from '../core/multi-select';
import NetworkSelect from '../app/network-select';
import ProviderSelect from '../app/provider-select';
import rsqlConfig from '../../../config/rsql-config';
import Select from '../core/select';
import ServiceSelect from '../app/service-select';
import ServicesExportPanelAction from '../app/services-export-panel-action';
import ServicesSearchForm from '../app/services-search-form';
import ServicesSearchPanelAction from '../app/services-search-panel-action';
import TableWithSort from '../core/table-with-sort';
import TooltipTrigger from '../core/tooltip-trigger';
import FvodOrderingTable from './fvod-ordering-table';

// Containers
import withSearch from '../../containers/with-search';

// Styles
import './media-items.scss';
import { columnConfig } from './helpers/constant';
import GenreSelect from '../app/genre-select';

const servicesDataKey = 'mediaItems';

function isFvodPastCutoff() {
    const fvodCutoffTime = Config.base.fvodCutoffTime || 17;
    return new Date().getHours() >= fvodCutoffTime;
}

class MediaItems extends Component {
  state = { duplicateDataReport: false, fvodOrderingMode: false, showFvodCutoffWarning: false };

  fvodOrderingTableRef = React.createRef();

  onIsDuplicateDataReport = (duplicateDataReport) => {
    this.setState({ duplicateDataReport: duplicateDataReport });
  };

  onFvodOrderingChange = (fvodOrderingMode) => {
    this.setState({ fvodOrderingMode });
  };

  onSaveOrder = () => {
    if (isFvodPastCutoff()) {
      this.setState({showFvodCutoffWarning: true});
      return;
    }
    if (this.fvodOrderingTableRef.current) {
      this.fvodOrderingTableRef.current.saveOrder();
    }
  };

  onConfirmSaveOrder = () => {
    this.setState({showFvodCutoffWarning: false});
    if (this.fvodOrderingTableRef.current) {
      this.fvodOrderingTableRef.current.saveOrder();
    }
  };

  onDismissCutoffWarning = () => {
    this.setState({showFvodCutoffWarning: false});
  };

  onCancelOrder = () => {
    if (this.fvodOrderingTableRef.current) {
      this.fvodOrderingTableRef.current.cancelOrder();
    }
  };

  onRefreshFvodData = () => {
    if (this.fvodOrderingTableRef.current) {
      this.fvodOrderingTableRef.current.fetchFvodData();
    }
  };

  getColumnConfig() {
    return columnConfig(this.getLockedIndicatorProps, this.getDetailLinkPrefix(), this.renderStateAndMapped);
  }

  getDetailLinkPrefix() {
    const { dashboardProps, detailLinkPrefix } = this.props;

    return get(dashboardProps, 'detailLinkPrefix') || detailLinkPrefix || '/media-items';
  }

  getFilteredTablewithsortProps() {
    return {
      columnConfig: this.getColumnConfig(),
      rowDetails: this.getRowDetails(),
      ...this.props,
      localSort: true,
      resetSort: null,
      setSort: null,
    };
  }

  getLockedIndicatorProps = ({ guid }) => ({
    className: 'media-items--lock',
    lockId: guid,
    serviceKey: servicesDataKey,
  });

  getMassActionOptions() {
    return [
      {
        formName: 'mediaItemsRepublish',
        label: 'Republish Selected Items',
      },
      {
        formName: 'mediaItemsReenrich',
        label: 'Re-Enrich Selected Items',
      },
      {
        allowRoles: ['mediaItem_sunset'],
        formName: 'mediaItemsSunset',
        label: 'Sunset Selected Items',
      },
    ];
  }

  getMultiSelectProps() {
    return {
      multi: true,
      operator: '=in=',
      placeholder: 'Filter by Priority',
      options: map(['IMMEDIATE', 'URGENT', 'HIGH', 'NORMAL', 'LOW'], (value) => ({
        value,
        label: value,
      })),
    };
  }

  getPaginationProps() {
    const { query, totalPages } = this.props;

    return {
      maxResults: 10000,
      maxPages: 9,
      query,
      totalPages,
    };
  }

  getResponseTablewithsortProps() {
    return {
      columnConfig: [
        {
          dataKey: 'target',
          title: 'Guid',
          link: '/media-items',
          linkKey: 'target',
          type: 'link',
          width: '35%',
        },
        {
          dataKey: 'statusPhrase',
          title: 'Status',
          width: '15%',
        },
        {
          dataKey: 'description',
          title: 'Title',
          width: '50%',
        },
      ],
    };
  }

  getSearchExportPanelProps = (searchData) => {
    const { totalElements, query } = this.props;

    return {
      totalElements,
      query,
      searchData,
    };
  };

  getRowDetails() {
    return {
      whitelistKeys: [
        'description',
        'releaseYear',
        'originalAirDate',
        'seasonNumber',
        'episodeNumber',
        'duration',
        'rating',
        'lang',
        'genres',
        'createdBy',
        'lastModifiedBy',
      ],
    };
  }

  getTablewithsortProps() {
    return {
      columnConfig: this.getColumnConfig(),
      formName: servicesDataKey,
      rowDetails: this.getRowDetails(),
      servicesDataKey,
      ...this.props,
    };
  }

  hasTargets() {
    const { searchFilter } = this.props;

    return !isEmpty(get(searchFilter, 'targets'));
  }

  renderFilteredTablewithsort() {
    return this.hasTargets() ? <TableWithSort {...this.getFilteredTablewithsortProps()} /> : null;
  }

  renderForm = () => (
        <ServicesSearchForm advancedSearch={true} formName={servicesDataKey} servicesDataKey={servicesDataKey}>
            <Input label="Title/Show Name" name="title||showName" />
            <Checkbox label="Exact Match" name="exactSearch" value="true" />
            <LanguageSelect defaultOption="Filter by Language" label="Language" name="lang" />
            <ServiceSelect label="Services" name="service.serviceName" />
            <Select label="State" name="state">
                <option value="">Filter by State</option>
                <option value="enrichment">ENRICHMENT</option>
                <option value="encoding">ENCODING</option>
                <option value="pre_production">PRE PRODUCTION</option>
                <option value="production">PRODUCTION</option>
            </Select>
            <ProviderSelect label="Provider" name="provider.partnerId" />
            <GenreSelect className="display-inline-block" label="AMS Franchise Genres" name="amsFranchise.genres" />
            <GenreSelect className="display-inline-block" label="AMS Episode Genres" name="amsEpisode.genres" />
            <Input label="ID from Partner" name="idFromPartner" />
            <NetworkSelect label="Network" name="externalNetwork.name||externalNetwork.network.name" />
            <Input label="External Network" name="externalNetwork.name" />
            <Input label="GUID" name="guid||externalGuid" />
            <Input label="RUID" name="offerings.resourceId" />
            <Input label="Categories" name="categories.value" />
            <Input label="Correlation ID" name="correlationId" />
            <Select label="Clip" name="expression">
                <option value="">Filter by Clip</option>
                <option value="clip">Show clips only</option>
                <option value="!clip">Hide all clips</option>
            </Select>
            <Input label="Echostar ID" name="amsFranchise.echostarId||amsEpisode.echostarId" validate={['number']} />
            <Input label="Asset Echostar3 ID" name="amsEpisode.echostarEventString" />
            <Input label="Franchise Echostar3 ID" name="amsFranchise.echostarEventString" />
            <Input
                label="Rovi Program ID"
                name="amsFranchise.roviProgramId||amsEpisode.roviProgramId"
                validate={['number']}
            />
            <Input label="Tms ID" name="amsFranchise.tmsId||amsEpisode.tmsId" />
            <Input label="Source ID" name="amsFranchise.sourceId||amsEpisode.sourceId" />
            <Select label="Ingest Source" name="amsFranchise.ingestSource||amsEpisode.ingestSource">
                <option value="">Filter by Ingest Source</option>
                <option value="gn">GN</option>
                <option value="tivo">TIVO</option>
                <option value="dish ">DISH</option>
            </Select>
            <Input
                label="EIDR"
                name="externalEidrId||parentEidrId||amsFranchise.eidrId||amsEpisode.eidrId"
                trim={true}
            />
            <Input
                label="Mapped AMS Title"
                name="amsFranchise.title||amsFranchise.seriesTitle||amsEpisode.title||amsEpisode.seriesTitle"
            />
            <Select label="Missing Image" name="amsFranchise.images.category">
                <option value="">Filter by Missing Image</option>
                <option value="!Franchise">Missing Franchise images</option>
                <option value="!TitleArt">Missing Title Art images</option>
                <option value='=not_in=("Franchise","TitleArt")'>Missing Title Art and Franchise images</option>
                <option value='=not_in=("Movie16x9","Series16x9","Special16x9")'>
                    Missing any Franchise 16:9 images
                </option>
                <option value='=rsql=(amsEpisode.images.category!="EpisodeBackground16x9");amsFranchise.images.category=out=("MovieBackground16x9","SeriesBackground16x9","SpecialBackground16x9")'>
                    Missing any Background 16:9 images
                </option>
                <option value='=rsql=(amsEpisode.images.category!=" EpisodeBackground16x9");amsFranchise.images.category=out=("Movie16x9","Series16x9","Special16x9","MovieBackground16x9","SeriesBackground16x9","SpecialBackground16x9")'>
                    Missing any 16:9 images
                </option>
            </Select>
            <Select label="Active" name="activeOnly">
                <option value="">Filter by Active</option>
                <option value="true">Active Only</option>
                <option value='=rsql=inactiveOnly=="true"'>Inactive Only</option>
            </Select>
            <Select label="AMS Shell" name="shellOnly">
                <option value="">Filter by Shell</option>
                <option value={`=rsql=${rsqlConfig.isShellAmsFranchise}`}> Franchise Shells Only </option>
                <option value={`=rsql=${rsqlConfig.isShellEpisode}`}> Episode Shells Only </option>
                <option value={`=rsql=((${rsqlConfig.isShellAmsFranchise}),${rsqlConfig.isShellEpisode})`}>
                    Franchise / Episode Shells
                </option>
            </Select>
            <Select
                label="AMS Content Type"
                name="amsFranchise.echostarContentType"
                title="Filter content by AMS Franchise Echostar Content Type"
            >
                <option value="">Filter by Type</option>
                <option value="MOV">Movies</option>
                <option value="SRM">Shows / Episodes</option>
                <option value="SPM">Special</option>
            </Select>
            <Select label="Auto-Mapped Reason" name="autoMappedReason">
                <option value="">Filter by Auto-Mapped Reason</option>
                <option value="Echostar">Echostar ID</option>
                <option value="Echostar3">Echostar3 ID</option>
                <option value="TMS">TMS ID</option>
                <option value="EIDR">EIDR ID</option>
                <option value="Rovi">Rovi ID / AMS ID</option>
                <option value="REPLAY">Map & Remember Rule</option>
                <option value="joint">DISH Group Joint Mapping</option>
                <option value="notNull">All Auto-Mapped</option>
                <option value="isNull">None</option>
            </Select>
            <DateRange label="Last Updated" name="lastUpdated" showTime={true} />
            <DateRange label="Offerings Start Date" name="offerings.startDate" />
            <DateRange label="Offerings End Date" name="offerings.endDate" />
            <DateRange label="Last Mapping Time" name="lastMappingTime" showTime={true} />
            <DateRange label="Date Published" name="publishDate" showTime={true} />
            <Select label="Offerings Type" name="offerings.type">
                <option value="">Select a Type</option>
                <option value="SVOD">SVOD - Subscription</option>
                <option value="TVOD">TVOD - Rental</option>
                <option value="FVOD">FVOD - Free</option>
                <option value="FREE">FREE</option>
                <option value="EST">EST - Purchase</option>
            </Select>
            <Select label="IVI Status" name="iviStatus">
                <option value="">Filter by Status</option>
                <option value="not_registered">NOT REGISTERED</option>
                <option value="pending">PENDING</option>
                <option value="success">SUCCESS</option>
                <option value="failed">FAILED</option>
                <option value="isNull">N/A</option>
            </Select>
            <Checkbox label="DAI Only" name="daiProviderId&&adSlots" value="notNull" />
            <MultiSelect label="Priority" name="priority" {...this.getMultiSelectProps()} />
            <Select label="Shells Extras" name="shellsExtras">
                <option value={`=rsql=${rsqlConfig.shellsExtrasIgnore}`}>Without Filtering by Shell Extras</option>
                <option value={`=rsql=${rsqlConfig.shellsExtrasHide}`}>Without Shell Extras Only</option>
                <option value={`=rsql=${rsqlConfig.shellsExtrasShow}`}>With Shells Extras Only</option>
            </Select>
        </ServicesSearchForm>
  );

  renderDuplicateForm = () => (
        <ServicesSearchForm formName={servicesDataKey} servicesDataKey={servicesDataKey}>
            <ServiceSelect label="Services" multi={false} name="service.const.serviceName" />
            <Select
                label="AMS Content Type"
                name="amsFranchise.const.echostarContentType"
                title="Filter content by AMS Franchise Echostar Content Type"
            >
                <option value="">Filter by Type</option>
                <option value="MOV">Movies</option>
                <option value="SRM">Shows / Episodes</option>
                <option value="SPM">Special</option>
            </Select>
        </ServicesSearchForm>
  );

  renderStateAndMapped = (dataRow) => {
    const { amsEpisode, amsFranchise, autoMappedReason, fullyFranchiseMapped, state } = dataRow;
    const isMapped = () => {
      const franchiseType = get(amsFranchise, 'echostarContentType');
      let mappedResult;

      if (!isEmpty(amsEpisode)) {
        mappedResult = true;
      } else if (!isEmpty(amsFranchise)) {
        mappedResult = franchiseType !== 'SRM' || fullyFranchiseMapped;
      }

      return mappedResult;
    };
    const tooltipRenderer = () => <div className="media-items--tooltip">{autoMappedReason}</div>;
    const getMappedIcon = () => (autoMappedReason ? (
                <TooltipTrigger displayDirection="up" tooltipRenderer={tooltipRenderer}>
                    <Icon className="media-items--mapped-icon" fontType="fa" iconName="location-arrow" />
                </TooltipTrigger>
            ) : (
                <Icon fontType="fa" iconName="map-marker" title="mapped" />
            ));

    return (
            <div style={{ whiteSpace: 'nowrap' }}>
                {isMapped() ? getMappedIcon() : null}
                {state}
            </div>
    );
  };

  render() {
    const { formValues, searchData } = this.props;
    const { duplicateDataReport, fvodOrderingMode, showFvodCutoffWarning } = this.state;
    const enableReportButton =            duplicateDataReport
            && (!formValues.amsFranchise?.const?.echostarContentType || !formValues?.service?.const?.serviceName?.length);
    const tableWithSortClassName = classnames(
      {
        'media-items--search-table_hide': this.hasTargets(),
      },
      'media-items--search-table'
    );

    return (
            <div className="media-items">
                <div className={tableWithSortClassName}>
                    <ServicesExportPanelAction
                        childKeys={['duplicate titles', 'FVOD Ordering']}
                        {...this.getSearchExportPanelProps(searchData)}
                        enableReportButton={enableReportButton}
                        onIsDuplicateDataReport={this.onIsDuplicateDataReport}
                        onCancelOrder={this.onCancelOrder}
                        onFvodOrderingChange={this.onFvodOrderingChange}
                        onRefreshFvodData={this.onRefreshFvodData}
                        onSaveOrder={this.onSaveOrder}
                    />
                    {fvodOrderingMode ? (
                        <FvodOrderingTable
                            ref={this.fvodOrderingTableRef}
                            detailLinkPrefix={this.getDetailLinkPrefix()}
                            getLockedIndicatorProps={this.getLockedIndicatorProps}
                            renderStateAndMapped={this.renderStateAndMapped}
                        />
                    ) : duplicateDataReport ? (
                        <ServicesSearchPanelAction contentRenderer={this.renderDuplicateForm} showPanel={true} />
                    ) : (
                        <>
                            <ServicesSearchPanelAction contentRenderer={this.renderForm} />
                            <MassActionTable
                                actionIdKey="guid"
                                massActionName={servicesDataKey}
                                massActionOptions={this.getMassActionOptions()}
                                paginationProps={this.getPaginationProps()}
                                responseTablewithsortProps={this.getResponseTablewithsortProps()}
                                serviceKey="mediaItems"
                                tableWithSortProps={this.getTablewithsortProps()}
                            />
                        </>
                    )}
                </div>
                {this.renderFilteredTablewithsort()}
                {showFvodCutoffWarning && (
                    <div className="mass-action-confirm">
                        <div className="mass-action-confirm--overlay-wrapper">
                            <div className="mass-action-confirm--overlay" style={{height: 'auto', minWidth: 'auto', padding: '15px 20px', width: '450px'}}>
                                <Button className="mass-action-confirm--close" type="link" onClick={this.onDismissCutoffWarning}>
                                    <Icon fontType="fa" iconName="close" />
                                </Button>
                                <div className="mass-action-confirm--label">FVOD Ordering Warning</div>
                                <div className="mass-action-confirm--text">
                                    FVOD ordering submitted after 5:00 PM on the day before the spool is not guaranteed to be included in the next RMS spool.
                                </div>
                                <Button className="mass-action-confirm--button" onClick={this.onConfirmSaveOrder}>
                                    Continue
                                </Button>
                                <Button className="mass-action-confirm--button" onClick={this.onDismissCutoffWarning}>
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
    );
  }
}

MediaItems.propTypes = {
  activeRowKeys: PropTypes.array,
  dashboardProps: PropTypes.object,
  detailLinkPrefix: PropTypes.string,
  echostarId: PropTypes.string,
  formValues: PropTypes.object,
  query: PropTypes.object,
  searchData: PropTypes.array,
  searchFilter: PropTypes.object,
  totalElements: PropTypes.number,
  totalPages: PropTypes.number,
};

export default withSearch(MediaItems, servicesDataKey);
